import { Model, Parser, CartSelection, ProductNode } from '@datamodel';
import { objectMap, mapObjectValues, objectFilter, objectReduce } from '@utils/helpers'; 

export class Coupon extends Model {

    static get uuid() { return 'couponId'; }

    static get model() {
        return {
            amountOff: 0,
            percentOff: 0,
            placeId: '',
        }
    }

    static get keymap() {
        return {
            place: 'placeId',
            id: 'couponId',
        }
    }

    constructor(args) {
        super(args);
        Object.assign(this, Coupon.unserialize({ ...Coupon.model, ...args }, { toClass: true }))
    }
}


export class PromotionCode extends Model {

    static get uuid() { return 'promotionCodeId'; }

    static get model() {
        return {
            coupon: Coupon.model,
            userId: '',
            code: '',
            expiredAt: new Date(),
            maxRedemptions: 0,
            minimumAmount: 0,
            stackable: false,
            firstTimeTransaction: false,
            maxRedemptionsPerUser: 0,
        }
    }

    static get keymap() {
        return {
            id: 'promotionCodeId',
            user: 'userId',
        }
    }

    static get parseMap() {
        return {
            expiredAt: d => Parser.date(d)
        }
    }

    static get unserialMap() {
        return {
            coupon: coupon => Coupon.unserialize(coupon),
        }
    }

    static get toClassMap() {
        return {
            coupon: coupon => new Coupon(coupon)
        }
    }

    constructor(args) {
        super(args);
        Object.assign(this, PromotionCode.unserialize({ ...PromotionCode.model, ...args }, { toClass: true }))
    }
}

export class Discount extends Model {

    static get uuid() { return 'discountId'; }

    static get model() {
        return {
            promotionCode: PromotionCode.model,
            coupon: Coupon.model,
        }
    }

    static get keymap() {
        return {
            id: 'discountId',
        }
    }


    static get parseMap() {
        return {
            promotionCode: pc => PromotionCode.parse(pc),
            coupon: c => Coupon.parse(c)
        }
    }

    static get unserialMap() {
        return {
            promotionCode: pc => PromotionCode.unserialize(pc),
            coupon: c => Coupon.unserialize(c)
        }
    }

    static get toClassMap() {
        return {
            promotionCode: pc => new PromotionCode(pc),
            coupon: c => new Coupon(c)
        }
    }

    constructor(args) {
        super(args);
        Object.assign(this, Discount.unserialize({ ...Discount.model, ...args }, { toClass: true }))
    }
}

export class Purchase extends Model {

    static get IN_PROGRESS() { return 'in_progress'; }
    static get PROCESSING() { return 'processing'; }
    static get INVALID() { return 'invalid'; }
    static get REDEEMABLE() { return 'redeemable'; }
    static get USED() { return 'used'; }

    static get REFUNDED() { return 'refunded'; }
    static get TRANSFERRED() { return 'transferred'; }

    static get uuid() { return 'purchaseId'; }

    static get model() {
        return {
            amount: 0,
            selection: CartSelection.model,
            discounts: {},
            amountOff: 0,
            total: 0,
            // _selectionId: '',
            status: Purchase.IN_PROGRESS,
            type: 'drink',
            invalidReason: null,
            token: '',
            menuNodeId: '',
            usedAt: null,
        }
    }

    static get keymap() {
        return {
            id: 'purchaseId',
            product_selection: 'selection',
            customer_total: 'total',
            menu_root: 'menuNodeId'
        }
    }

    static get parseMap() {
        return {
            discounts: discounts => objectMap(
                discounts.map(d => Discount.parse(d)),
                Discount.uuid
            ),
            selection: sel => CartSelection.parse(sel),
            usedAt: d => Parser.date(d)
        }
    }

    static get unserialMap() {
        return {
            discounts: discounts => mapObjectValues(
                discounts,
                d => Discount.unserialize(d)
            ),
            selection: sel => CartSelection.unserialize(sel),
        }
    }

    static get toClassMap() {
        return {
            discounts: discounts => mapObjectValues(
                discounts,
                d => new Discount(d)
            ),
            selection: sel => new CartSelection(sel),
        }
    }

    constructor(args) {
        super(args);
        Object.assign(this, Purchase.unserialize({ ...Purchase.model, ...args }, { toClass: true }))
    }

    static hasRedeemableType(purchase, type) {
        return purchase.status === 'redeemable' && purchase.type === type;
    }


    static addSelectionId(purchase) {
        purchase._selectionId = CartSelection.getId(purchase.selection);
        return purchase;
    }

    static stackPurchases(purchases) {
        
        return Object.keys(purchases).reduce((prev, nextKey) => {

            const next = Purchase.addSelectionId(purchases[nextKey]);
            const selId = next._selectionId;

            if (selId in prev) {
                prev[selId].quantity++;
            } else {
                prev[selId] = { ...next, quantity: 1 };
            }

            return prev;
        }, {});
    }

    static getColor(purchase) {
        switch (purchase.status) {
            case Purchase.REDEEMABLE:
                return 'primary';
            case Purchase.INVALID:
                if (purchase.invalidReason === Purchase.REFUNDED)
                    return 'warning';
                return 'label';
            case Purchase.USED:
                return 'success';
            default:
                return 'label';
        }
    }

    static getSelectionLeafs(purchase) {
        return CartSelection.getLeafs(purchase.selection);
    }

    static addMainSelection(purchase, fungibleNode) {
        CartSelection.addMainSelection(purchase.selection, fungibleNode);
        return purchase;
    }

    static getNodeId(purchase) {
        return purchase.selection.item.nodeId;
    }

    // Gets a new server representation of the purchase selection.
    // It's intended to be used to generate a cart from a past order,
    // that's why ignoreId is true by default
    static getServerSelections(purchase, { ignoreId } = { ignoreId: true }) {
        return CartSelection.toServer(purchase.selection, { ignoreId });
    }

    static isRepeatable(purchase, menu) {
        return purchase.menuNodeId === menu.nodeId && CartSelection.hasStock(purchase.selection, menu);
    }
    
}

export class Order extends Model {

    static get IN_PROGRESS() { return 'in_progress'; }
    static get PROCESSING() { return 'processing'; }
    static get ON_GOING() { return 'on_going'; }
    static get COMPLETED() { return 'completed'; }
    static get SUCCEEDED() { return 'succeeded'; }
    static get INVALID() { return 'invalid'; }

    static get REFUNDED() { return 'refunded'; }
    static get TRANSFERRED() { return 'transferred'; }

    static get uuid() { return 'orderId'; }

    static get model() {
        return {
            userId: '',
            eventId: '',
            status: '',
            amount: 0,
            purchases: { },
            discounts: { },
            total: 0,
            festoFee: 0,
            status: '',
            reasonInvalid: null,
            succeededAt: new Date(),
            containsTicket: false,
        }
    }

    static get keymap() {
        return {
            user: 'userId',
            event: 'eventId',
            id: 'orderId',
            customer_total: 'total',
        }
    }

    static get parseMap() {
        return {
            // Will group purchases in parseCallback
            purchases: purchases => objectMap(
                purchases.map(p => Purchase.parse(p)),
                Purchase.uuid),
            discounts: discounts => objectMap(
                discounts.map(d => Discount.parse(d)),
                Discount.uuid
            ),
            succeededAt: d => Parser.date(d)
        }
    }

    static get parseCallback() {
        return (order) => {

            if ( Object.keys(order.purchases).some( pkey => order.purchases[pkey].type === 'ticket' )) {
                order.containsTicket = true;
            }

            let allStatus = Object.keys(order.purchases).map(pkey => ({
                status: order.purchases[pkey].status,
                invalidReason: order.purchases[pkey].invalidReason
            }));

            // If some purchase is in_progress the order is still in_progress
            if ( allStatus.some(s => s.status === Purchase.IN_PROGRESS )) {
                order.status = Order.IN_PROGRESS;
                return order;
            }

            // If some purchase is processing the order is still processing
            if (allStatus.some(s => s.status === Purchase.PROCESSING)) {
                order.status = Order.PROCESSING;
                return order;
            }

            // If every purchase is invalid
            if ( allStatus.every(s => s.status === Purchase.INVALID) ) {
                order.status = Order.INVALID;
                // If every status is refunded, reason is refunded
                if ( allStatus.every(s => s.reasonInvalid === Purchase.REFUNDED ) ) {
                    order.reasonInvalid = Order.REFUNDED;
                } else if ( allStatus.every(s => s.reasonInvalid === Purchase.TRANSFERRED )) {
                    order.reasonInvalid = Order.TRANSFERRED;
                }
                return order;
            }

            // Else, filter out invalid statuses to find out order status
            allStatus = allStatus.filter(s => s.status !== Purchase.INVALID );

            if ( allStatus.every(s => s.status === Purchase.REDEEMABLE )) {
                order.status = Order.SUCCEEDED;
            } else if (allStatus.every(s => s.status === Purchase.USED )) {
                order.status = Order.COMPLETED;
            } else {
                order.status = Order.ON_GOING;
            }

            return order;
        }
    }

    static stackPurchases(order) {
            // Group purchases by _selectionId
            const newOrder = { ...order };
            newOrder.purchases = Purchase.stackPurchases(newOrder.purchases);
            return newOrder;
    }

    static get unserialMap() {
        return {
            purchases: purchases => mapObjectValues(
                purchases,
                p => Purchase.unserialize(p)
            ),
            discounts: discounts => mapObjectValues(
                discounts,
                d => Discount.unserialize(d)
            ),
        }
    }

    static get toClassMap() {
        return {
            purchases: purchases => mapObjectValues(
                purchases,
                p => new Purchase(p)
            ),
            discounts: discounts => mapObjectValues(
                discounts,
                d => new Discount(d)
            ),
        }
    }

    constructor(args) {
        super(args);
        Object.assign(this, Order.unserialize({ ...Order.model, ...args }, { toClass: true }))
    }

    static hasRedeemable(order) {

        return Object.keys(order.purchases).reduce((result, nextKey) => {
            const purchase = order.purchases[nextKey];

            if ( Purchase.hasRedeemableType(purchase, 'ticket') ) {
                result.hasTickets = true;
            }
            else if ( Purchase.hasRedeemableType(purchase, 'drink') ) {
                result.hasDrinks = true;
            }
            return result;

        }, { hasTickets: false, hasDrinks: false });

    }

    static getRedeemableTokens(order, type) {
        
        return Object.keys(order.purchases).flatMap(pkey => {
            const purchase = order.purchases[pkey];
            if ( purchase.status === 'redeemable' && purchase.type === type) {
                return [purchase.token];
            }
            return [];
        });

    }

    static getColor(order) {
        switch (order.status) {
            case Order.SUCCEEDED:
                return 'primary';
            case Order.ON_GOING:
                return 'Dodger Blue';
            case Order.INVALID:
                if (order.invalidReason === Order.REFUNDED)
                    return 'warning';
                return 'label';
            case Order.COMPLETED:
                return 'success';
            default:
                return 'label';
        }
    }

    static getMenuIds(order) {
        const menus = new Set();
        Object.keys(order.purchases).forEach(pId => {
            menus.add(order.purchases[pId].menuNodeId);
        })
        return [...menus];
    }

    static collectionFilterByEvent(orderCollection, eventId) {
        return objectFilter(orderCollection, o => o.eventId === eventId);
    }

    static collectionSplitByRedeemable(orderCollection) {
        const orders = { redeemables: {}, unredeemables: {}, completed: {}}

        for (const orderId in orderCollection) {
            const o = orderCollection[orderId];
            if (o.status === Order.ON_GOING || o.status === Order.SUCCEEDED) {
                orders.redeemables[orderId] = o;
            } else if ( o.status === Order.COMPLETED ) {
                orders.completed[orderId] = o;
            } else {
                orders.unredeemables[orderId] = o;
            }
        }

        return orders;
    }

    static collectionSplitByType(orderCollection) {
        // TODO: Check wherever we use this cause
        // it might not be necessary
        const orders = { tickets: {}, products: {} }

        for (const orderId in orderCollection) {

            const { hasTickets } = this.hasRedeemable(orderCollection[orderId]);

            if (hasTickets) {
                orders.tickets[orderId] = orderCollection[orderId];
            }
        }

        return orders;
    }

    static addMainSelections(order, menu) {
        const fungibleNodes = ProductNode.getTreeNodes(menu, Object.keys(order.purchases).map(pId => Purchase.getNodeId(order.purchases[pId])))
        Object.keys(order.purchases).forEach(pId => {
            const purchase = order.purchases[pId];
            Purchase.addMainSelection(purchase, fungibleNodes[Purchase.getNodeId(purchase)])
        })
        return order;
    }

    static getServerSelections(order) {
        return  Object.keys(order.purchases).map(pId => {
            return Purchase.getServerSelections(order.purchases[pId]);
        });
    }

    static isRepeatable(order, menu) {
        return Object.keys(order.purchases).reduce((status, pId) => {
            return status && Purchase.isRepeatable(order.purchases[pId], menu);
        }, true);
    }
}